<template>
  <global-layout>
    <div class="erp-page">
      <a-card size="small">
        <div class="monitor-header">
          <div>
            <div class="monitor-title">数据监控配置</div>
            <div class="monitor-description">统一配置各项数据监控的运行频率和启用状态。</div>
          </div>
          <a-button icon="reload" :loading="loading" @click="load">刷新</a-button>
        </div>
        <a-alert v-if="error" type="error" show-icon :message="error"/>
      </a-card>

      <a-card class="erp-section" size="small">
        <a-table rowKey="monitorCode" size="small" :columns="columns" :dataSource="rows" :loading="loading"
                 :pagination="false" :scroll="{x: 1050}" :locale="{emptyText: '暂无监控事项'}">
          <template slot="enabled" slot-scope="value">
            <a-tag :color="value ? 'green' : 'default'">{{ value ? '已启用' : '已停用' }}</a-tag>
          </template>
          <template slot="status" slot-scope="value">
            <a-tag :color="statusColor(value)">{{ statusText(value) }}</a-tag>
          </template>
          <template slot="action" slot-scope="value, record">
            <a-space>
              <a @click="edit(record)">设置</a>
              <a :class="{disabled: !record.enabled || runningCode === record.monitorCode}"
                 @click="run(record)">{{ runningCode === record.monitorCode ? '执行中' : '立即执行' }}</a>
            </a-space>
          </template>
        </a-table>
      </a-card>

      <a-modal title="监控事项配置" :visible="configVisible" :maskClosable="false" :confirmLoading="saving"
               @ok="save" @cancel="!saving && (configVisible = false)">
        <a-form-model layout="vertical">
          <a-form-model-item label="监控名称">
            <a-input :value="form.monitorName" disabled/>
          </a-form-model-item>
          <a-form-model-item label="监控编码">
            <a-input :value="form.monitorCode" disabled/>
          </a-form-model-item>
          <a-form-model-item label="运行频率（分钟）" :required="form.enabled">
            <a-input-number v-model="form.frequencyMinutes" :min="1" :max="10080" :precision="0"
                            style="width: 180px"/>
          </a-form-model-item>
          <a-form-model-item>
            <a-switch v-model="form.enabled" checkedChildren="启用" unCheckedChildren="停用"/>
          </a-form-model-item>
        </a-form-model>
        <a-alert type="info" show-icon
                 message="停用后不再产生新的监控执行；启用时必须设置运行频率。当前库存监控仅消费成功的夜间 BI 重建结果。"/>
      </a-modal>
    </div>
  </global-layout>
</template>

<script>
import {getAction, postAction} from '@/api/manage'
import GlobalLayout from '@/components/page/GlobalLayout'

export default {
  name: 'DataMonitorList',
  components: {GlobalLayout},
  data() {
    return {
      rows: [],
      loading: false,
      saving: false,
      runningCode: '',
      configVisible: false,
      form: {},
      error: '',
      disposed: false,
      columns: [
        {title: '监控事项', dataIndex: 'monitorName'},
        {title: '监控编码', dataIndex: 'monitorCode', width: 260},
        {title: '运行频率（分钟）', dataIndex: 'frequencyMinutes', width: 150},
        {title: '启用状态', dataIndex: 'enabled', width: 110, scopedSlots: {customRender: 'enabled'}},
        {title: '下次检查时间', dataIndex: 'nextRunTime', width: 170},
        {title: '最近执行状态', dataIndex: 'lastStatus', width: 130, scopedSlots: {customRender: 'status'}},
        {title: '最近执行时间', dataIndex: 'lastExecutionTime', width: 170},
        {title: '操作', key: 'action', width: 145, fixed: 'right', scopedSlots: {customRender: 'action'}}
      ]
    }
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    this.disposed = true
  },
  methods: {
    statusText(value) {
      if (value === 'PASSED') return '通过'
      if (value === 'ANOMALY') return '发现异常'
      if (value === 'FAILED') return '执行失败'
      return value || '尚未执行'
    },
    statusColor(value) {
      if (value === 'PASSED') return 'green'
      if (value === 'ANOMALY') return 'orange'
      if (value === 'FAILED') return 'red'
      return 'default'
    },
    async load() {
      if (this.loading || this.disposed) return
      this.loading = true
      try {
        const res = await getAction('/system/dataMonitor/tasks')
        if (this.disposed) return
        if (!res.success) throw new Error(res.message)
        this.rows = res.result || []
        this.error = ''
      } catch (e) {
        if (!this.disposed) this.error = e.message || '读取数据监控配置失败'
      } finally {
        if (!this.disposed) this.loading = false
      }
    },
    edit(row) {
      this.form = {...row, enabled: !!row.enabled}
      this.configVisible = true
    },
    async save() {
      if (this.form.enabled && (!Number.isInteger(this.form.frequencyMinutes) || this.form.frequencyMinutes < 1)) {
        this.$message.warning('启用监控前请填写正整数运行频率')
        return
      }
      if (this.saving) return
      this.saving = true
      try {
        const data = {...this.form, frequencyMinutes: this.form.enabled ? this.form.frequencyMinutes : null}
        const res = await postAction('/system/dataMonitor/saveTask', data)
        if (!res.success) throw new Error(res.message)
        this.configVisible = false
        this.$message.success(res.message || '监控配置已保存')
        await this.load()
      } catch (e) {
        this.$message.error(e.message || '保存监控配置失败')
      } finally {
        this.saving = false
      }
    },
    async run(row) {
      if (!row.enabled || this.runningCode) return
      this.runningCode = row.monitorCode
      try {
        const res = await postAction('/system/dataMonitor/run?monitorCode=' + encodeURIComponent(row.monitorCode), {})
        if (!res.success) throw new Error(res.message)
        this.$message.success(res.result || res.message || '监控执行完成')
        await this.load()
      } catch (e) {
        this.$message.error(e.message || '监控执行失败')
      } finally {
        this.runningCode = ''
      }
    }
  }
}
</script>

<style scoped>
.erp-page {
  padding: 8px;
  background: #f5f5f5;
}

.erp-section {
  margin-top: 8px;
}

.monitor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.monitor-title {
  font-size: 16px;
  font-weight: 600;
}

.monitor-description {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.45);
}

.disabled {
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}
</style>
